<template>
  <!--
    ⛔ The scrim does NOT close on click when there is unsaved work. It used to emit `close`
    unconditionally, so one stray click anywhere outside the card silently threw away a
    half-configured settings blob — reported after someone lost a procedure-code selection
    they were partway through. Clicking outside is the easiest gesture in the UI to perform by
    accident and the hardest to undo, because nothing was persisted to undo back to.
    With nothing unsaved it still closes, which is what people expect from a scrim.
  -->
  <div v-if="open && isAdminOrAbove" class="ivs-scrim" @click="requestClose">
    <div class="ivs-modal" role="dialog" aria-modal="true" @click.stop>
      <!-- Header -->
      <div class="ivs-head">
        <span class="ivs-head-icon"><i class="fa fa-shield" aria-hidden="true"></i></span>
        <div class="ivs-head-meta">
          <h2 class="ivs-title">Precheck settings</h2>
          <p class="ivs-sub">Automatically verify eligibility before appointments.</p>
        </div>
        <button type="button" class="ivs-close" aria-label="Close" @click="requestClose">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Tabs -->
      <div class="ivs-tabs">
        <button
          type="button"
          class="ivs-tab"
          :class="{ 'is-active': activeTab === 'settings' }"
          @click="activeTab = 'settings'"
        >Automation</button>
        <button
          type="button"
          class="ivs-tab"
          :class="{ 'is-active': activeTab === 'payers' }"
          @click="activeTab = 'payers'"
        >Payer mapping</button>
      </div>

      <!-- Body -->
      <div class="ivs-body">
        <!-- Settings tab -->
        <div v-show="activeTab === 'settings'">
          <!-- Loading settings -->
          <div v-if="loading" class="ivs-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading settings…
          </div>

          <!-- Load error -->
          <div v-else-if="loadError" class="ivs-state ivs-state--error">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <span>{{ loadError }}</span>
            <button type="button" class="ivs-retry" @click="loadSettings(true)">Retry</button>
          </div>

          <template v-else>
            <!-- Usage this month. Deliberately the FIRST thing in the settings tab: every
                 control below this line changes how much gets spent, and the office should
                 read the meter before turning a dial, not after the invoice. -->
            <div v-if="usage" class="ivs-usage">
              <div class="ivs-usage-head">
                <span class="ivs-usage-title">Checks used this month</span>
                <span class="ivs-usage-month">{{ usage.month }}</span>
              </div>
              <div class="ivs-usage-figure">
                <!-- A RANGE, not a number. `unknown` checks are ones we cannot yet prove
                     reached the payer; showing a single total would either overstate what
                     the practice owes or hide real spend. When the band is tight (nothing
                     uncertain) it collapses to one figure on its own. The band / plural rule
                     now lives in the shared `usageMeterView` (constants.js), which the
                     Credits-tab Usage card also renders — same money-copy, one source. -->
                <strong>{{ usageView.headline }}</strong>
                <span class="ivs-usage-unit">{{ usageView.unit }}</span>
              </div>
              <div class="ivs-usage-split">
                <span>{{ splitBillable('nightly') }} automatic</span>
                <span>·</span>
                <span>{{ splitBillable('onDemand') }} manual</span>
                <span v-if="splitBillable('onBooking')">·</span>
                <span v-if="splitBillable('onBooking')">{{ splitBillable('onBooking') }} on booking</span>
                <span v-if="usageView.free">·</span>
                <span v-if="usageView.free">{{ usageView.free }} refused free</span>
              </div>
              <div v-if="usageView.unknownNote" class="ivs-usage-note">
                {{ usageView.unknownNote }}
              </div>
            </div>

            <!-- Automatic precheck master toggle -->
            <div class="ivs-row">
              <div class="ivs-row-text">
                <div class="ivs-row-title ivs-row-title--lg">Automatic precheck</div>
                <div class="ivs-row-desc">
                  Run eligibility checks on a schedule without manual work. Options below apply
                  when this is on.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.autoPrecheckEnabled" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>

            <!-- Auto-precheck sub-options -->
            <div class="ivs-subsection" :class="{ 'is-off': !form.autoPrecheckEnabled }">
              <div class="ivs-block">
                <div class="ivs-row-title">How often should a patient be due for a verification</div>
                <!--
                  ⛔ NO `.number` MODIFIER. The values are strings now ('never', '7d', '6m').
                  `v-model.number` would coerce '7d' to NaN and silently reset the office's
                  choice to the default the next time the blob was built.
                -->
                <div class="ivs-control ivs-control--md">
                  <select class="iv-input" v-model="form.reuseWindow">
                    <option v-for="o in reuseOptions" :key="o.v" :value="o.v">{{ o.label }}</option>
                  </select>
                </div>
                <div class="ivs-help">
                  Before running, this reads the <strong>Eligibility Last Verified</strong> date in
                  the Open Dental Family module and this site's own check history — if either falls
                  inside the window you pick, the check is skipped and nothing is billed.
                  <template v-if="form.reuseWindow === 'never'">
                    <br />
                    Every appointment gets its own check. A patient is still only checked
                    <strong>once per appointment</strong>, not once per night.
                  </template>
                </div>
                <!-- Re-verify frequency is the single biggest cost lever in this modal —
                     the cost analysis measures sub-monthly re-verification at +33-60%
                     volume on its own. One dropdown can multiply the bill, so it says so
                     here rather than in an invoice line three weeks later. -->
                <div v-if="frequencyIsExpensive" class="ivs-cost-note">
                  <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                  <template v-if="form.reuseWindow === 'never'">
                    Every appointment is checked, so a patient with several visits close together
                    is checked before each one. Most practices are fine here — a schedule heavy on
                    multi-visit treatment will see noticeably more billed checks than
                    <strong>Every 6 months</strong>.
                  </template>
                  <template v-else>
                    Re-checking this often means many more billed checks — a patient seen
                    regularly can be re-verified several times a year instead of twice.
                    <strong>Every 6 months</strong> is the recommended setting.
                  </template>
                </div>
              </div>

              <div class="ivs-row ivs-row--bordered">
                <div class="ivs-row-text">
                  <div class="ivs-row-title">Verify before appointment</div>
                  <div class="ivs-row-desc">How many days ahead to run the check.</div>
                </div>
                <div class="ivs-stepper">
                  <button type="button" class="ivs-step" :disabled="form.lookaheadDays <= 0" @click="bumpLookahead(-1)">−</button>
                  <span class="ivs-step-val">{{ form.lookaheadDays }}d</span>
                  <button type="button" class="ivs-step" :disabled="form.lookaheadDays >= 14" @click="bumpLookahead(1)">+</button>
                </div>
              </div>

              <!-- Lookahead is NOT the lever people assume: the freshness gate means a
                   longer window mostly re-scans patients who are already verified, so it
                   is close to free — UNTIL a check fails, because a failed check has no
                   fresh date to suppress the next attempt and gets retried each day it
                   stays in the window. Say that, rather than a generic "costs more". -->
              <div v-if="lookaheadIsLong" class="ivs-cost-note">
                <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                A longer window is usually inexpensive — already-verified patients are
                skipped. The cost shows up on patients whose check <em>fails</em>: those get
                retried on each remaining day in the window. Fix flagged records in Open
                Dental before widening this.
              </div>

              <div class="ivs-block ivs-block--bordered">
                <div class="ivs-row-title">Time of day to verify insurance benefits</div>
                <div class="ivs-control ivs-control--sm">
                  <input type="time" class="iv-input" v-model="form.runTime" />
                </div>
                <!-- ⛔ Copy must name BOTH constraints. Until 2026-09-20 it said only "we
                     recommend off-hours", which is the reasoning that produced the old 02:00
                     default — and overnight is exactly when payer eligibility gateways are least
                     reachable. Measured at one prod site: 07:00 answers 78% of checks, and the
                     two biggest payers answer BETTER at 07:00 than during the day. Telling an
                     office to pick 2am reads as helpful and quietly costs them their morning
                     verifications. Early morning satisfies both constraints. -->
                <div class="ivs-help">
                  When automated verifications run each day. Appointments added after this time
                  need a manual Verify. Early morning works best — insurance companies' systems
                  are often unavailable overnight, and running before the day starts keeps benefit
                  PDFs out of Open Dental during busy hours.
                </div>
              </div>

              <div class="ivs-row">
                <div class="ivs-row-text">
                  <div class="ivs-row-title">Verify on new booking</div>
                  <!-- ⛔ Copy must stay TRUTHFUL about the mechanism: this is an HOURLY sweep
                       (7 AM–10 PM site-local, see the service's SWEEP_LOCAL_*_HOUR constants),
                       not an appointment-created event, and it only sees appointments inside
                       the verify-before window. "The moment an appointment is booked" was the
                       original wording and it overpromised on both counts. The insurance-added-
                       later clause is the answer to the front desk's most common question. -->
                  <div class="ivs-row-desc">Check new bookings hourly between 7 AM and 10 PM, once they're within your verify-before window. Insurance added after booking is picked up on the next hourly pass.</div>
                </div>
                <span class="iv-switch">
                  <input type="checkbox" v-model="form.verifyOnBooking" />
                  <span class="iv-switch-slider"></span>
                </span>
              </div>
            </div>

            <div class="ivs-divider"></div>

            <!-- Billing provider (required for the 270) -->
            <div class="ivs-block">
              <div class="ivs-row-title">Billing provider</div>
              <div class="ivs-row-desc">
                Sent as the billing provider on every eligibility request. Verification can't run
                until this is set.
              </div>
              <div class="ivs-control ivs-control--md">
                <select class="iv-input" v-model="billingProviderModel">
                  <option :value="null">Select a provider…</option>
                  <option v-for="p in providerOptions" :key="p.provNum" :value="p.provNum">{{ p.label }}</option>
                </select>
              </div>
              <div v-if="!providerOptions.length" class="ivs-help ivs-help--warn">
                No providers loaded for this practice.
              </div>
            </div>

            <div class="ivs-divider"></div>

            <!-- Procedure codes verified on every check -->
            <div class="ivs-block">
              <div class="ivs-row-title">Procedure codes</div>
              <div class="ivs-row-desc">
                The CDT procedure codes every eligibility check pulls coverage for. They drive
                both the benefits request and the per-code breakdown on the report.
              </div>

              <!--
                Options are the office's own Open Dental codes (with their descriptions), so
                staff can browse rather than recall the CDT set. `taggable` stays because these
                codes are sent to the PAYER, not to Open Dental — a practice can legitimately
                ask about a code they have never charted, and a dropdown alone would remove
                that. tag-position="bottom" keeps matching options above the "add" prompt, so
                typing a code the office DOES have selects it instead of duplicating it.
              -->
              <multiselect
                class="ivs-proc-select"
                :value="selectedProcOptions"
                :options="procCodeOptions"
                :multiple="true"
                :searchable="true"
                :taggable="true"
                tag-position="bottom"
                :close-on-select="false"
                :show-labels="false"
                :loading="procLoading"
                label="text"
                track-by="value"
                placeholder="Search or add a code (e.g. D0120)…"
                tag-placeholder="Add this code"
                @input="onProcSelectionChange"
                @tag="onProcTag"
                @search-change="procError = null"
              >
                <!-- Chips stay code-only: 42 defaults rendered as full descriptions would bury
                     the control. The description rides along as the hover title. -->
                <template slot="tag" slot-scope="{ option }">
                  <span class="ivs-chip" :title="option.desc || ''">
                    {{ option.value }}
                    <button
                      type="button"
                      class="ivs-chip-x"
                      :aria-label="'Remove ' + option.value"
                      @click.stop="removeProcedureCode(option.value)"
                    ><i class="fa fa-times" aria-hidden="true"></i></button>
                  </span>
                </template>
                <!-- Deliberately NOT "not a valid code": a real CDT code the office doesn't
                     carry in Open Dental also lands here, and it is perfectly askable. -->
                <template slot="noResult">No matching code — press enter to add it anyway.</template>
              </multiselect>

              <div v-if="procCustomized" class="ivs-inline ivs-proc-add">
                <button type="button" class="ivs-linkbtn" @click="resetProcedureCodes">Reset to default</button>
              </div>

              <div v-if="procError" class="ivs-help ivs-help--warn">{{ procError }}</div>
              <div v-else-if="!procCodes.length" class="ivs-help ivs-help--warn">
                No codes yet — add at least one above.
              </div>
              <div v-else-if="procLoadFailed" class="ivs-help">
                Couldn't load your Open Dental codes — you can still type codes manually.
              </div>
              <div v-else-if="!procCustomized" class="ivs-help">
                Teamio default set — add or remove codes for your office.
              </div>
            </div>

            <div class="ivs-divider"></div>

            <!-- Default benefits view (2026-09-29). Display only: which network a verification
                 opens on. Changes no check, no Open Dental write and no PDF. -->
            <div class="ivs-block">
              <div class="ivs-row-title">Default benefits view</div>
              <div class="ivs-row-desc">
                Which network's benefits a verification shows when you open it.
              </div>
              <div class="ivs-control ivs-control--md">
                <select v-model="form.benefitsNetworkView" class="iv-input" data-test="ivs-network-view">
                  <option v-for="o in networkViewOptions" :key="o.v" :value="o.v">{{ o.label }}</option>
                </select>
              </div>
              <div class="ivs-help">
                Choose <strong>Out-of-network only</strong> if your office is out of network with most
                carriers. Anyone can still switch views inside a verification, and Print / PDF
                always show both networks.
              </div>
            </div>

            <div class="ivs-divider"></div>

            <!-- Import PDF to OD + imaging folder -->
            <div class="ivs-row">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Import benefits PDF into Open Dental</div>
                <div class="ivs-row-desc">
                  File the verification handout into an Open Dental imaging category.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.importPdfToOD" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>
            <div v-if="form.importPdfToOD" class="ivs-block ivs-block--indent">
              <div class="ivs-row-title ivs-row-title--sm">Open Dental imaging folder</div>
              <div class="ivs-control ivs-control--md">
                <select class="iv-input" v-model="imagingCategoryModel">
                  <option :value="null">Select a folder…</option>
                  <option v-for="c in imagingCategories" :key="c.defNum" :value="c.defNum">{{ c.itemName }}</option>
                </select>
              </div>
              <div v-if="imagingLoading" class="ivs-help"><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading folders…</div>
              <div v-else-if="!imagingCategories.length" class="ivs-help ivs-help--warn">No imaging categories found in Open Dental.</div>
              <div v-else-if="form.imagingCategoryDefNum == null" class="ivs-help ivs-help--warn">Pick a folder so the PDF has somewhere to land.</div>
            </div>

            <div class="ivs-divider"></div>

            <!-- ═══ What gets recorded in Open Dental ═══════════════════════════════════
                 THREE independent switches since 2026-08-18. They were ONE ("Record results
                 in Open Dental", Super-Admin-locked), which meant an office could not keep
                 the Communication Log note without the date stamp, or vice versa, and could
                 not change either without calling support.

                 The lock is gone deliberately: it was UI-only and never enforced server-side
                 (saveSettings carries no role context), so it bought no safety — it only
                 stopped the office from configuring its own chart. Splitting one locked
                 toggle into three locked toggles would have given Katie nothing.

                 ⛔ Key semantics — `odWriteBack` NARROWED, it was not renamed. It now means
                 the eligibility stamp only; the note moved to `odWriteBackCommlog`. A blob
                 stored before the split has neither new key, so the backend's
                 applyOdWriteBackCompat (lib/settings-parse.js) inherits the note from the old
                 key — including inheriting an explicit OFF. Do not "tidy" that away. -->
            <div class="ivs-section-label">Record in Open Dental</div>

            <!-- Appointment Field Defs (2026-09-23) — a FOURTH independent write, deliberately
                 ABOVE Eligibility Last Verified. After a VERIFIED check, set one of the office's
                 own OD appointment fields (e.g. "Insurance Verification" = "VERIFIED") on the
                 CHECKED appointment — or, for a Patient Profile Verify with no appointment, on each
                 scheduled appointment in the next 30 days (backend resolves them). Option value is the FieldName STRING — that is what
                 OD's apptfield rows key on; ApptFieldDefNum is only the :key. -->
            <div class="ivs-row ivs-row--bordered">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Change value of Appointment Field Defs</div>
                <div class="ivs-row-desc">
                  After a check, set one of your Open Dental <strong>appointment fields</strong> on the
                  verified appointment.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.odWriteBackApptField" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>
            <div v-if="form.odWriteBackApptField" class="ivs-block ivs-block--indent">
              <div class="ivs-row-title ivs-row-title--sm">Appointment field</div>
              <div class="ivs-control ivs-control--md">
                <select class="iv-input" v-model="apptFieldNameModel">
                  <option :value="null">Select a field…</option>
                  <option v-if="apptFieldNameMissing" :value="form.apptFieldName">{{ form.apptFieldName }} (not found)</option>
                  <option v-for="f in apptFieldDefs" :key="f.apptFieldDefNum" :value="f.fieldName">{{ f.fieldName }}</option>
                </select>
              </div>
              <div v-if="apptFieldLoading" class="ivs-help"><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading appointment fields…</div>
              <div v-else-if="!apptFieldDefs.length" class="ivs-help ivs-help--warn">No appointment fields found in Open Dental — create them in Open Dental first.</div>
              <div v-else-if="apptFieldNameMissing" class="ivs-help ivs-help--warn">“{{ form.apptFieldName }}” no longer exists in Open Dental — pick another field.</div>

              <div class="ivs-row-title ivs-row-title--sm ivs-appt-value-label">Value to set</div>
              <div class="ivs-control ivs-control--md">
                <input
                  type="text"
                  class="iv-input"
                  maxlength="255"
                  placeholder="e.g. VERIFIED"
                  v-model="form.apptFieldValue"
                />
              </div>
              <div v-if="apptFieldError" class="ivs-help ivs-help--warn">{{ apptFieldError }}</div>
              <div v-else class="ivs-help">
                Only set when coverage is <strong>actually confirmed</strong>. A check run for an
                appointment sets that appointment; a check run from the patient's profile sets each of
                their <strong>scheduled appointments in the next 30 days</strong>.
              </div>
            </div>

            <div class="ivs-row ivs-row--bordered">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Eligibility Last Verified</div>
                <div class="ivs-row-desc">
                  After a check, set the patient's <strong>Eligibility Last Verified</strong> date.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.odWriteBack" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>
            <div v-if="form.odWriteBack" class="ivs-block ivs-block--indent">
              <div class="ivs-help">
                Only set when coverage is <strong>actually confirmed</strong>, so a plan that comes
                back inactive is re-checked again soon rather than looking verified for
                {{ reuseWindowLabel }}.
              </div>
            </div>

            <div class="ivs-row ivs-row--bordered">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Benefits Last Verified</div>
                <div class="ivs-row-desc">
                  After a check, set the plan's <strong>Benefits Last Verified</strong> date.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.odWriteBackBenefits" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>
            <div v-if="form.odWriteBackBenefits" class="ivs-block ivs-block--indent">
              <div class="ivs-help">
                <!-- Stated up front rather than left to be discovered: FKey for VerifyType 1
                     is the PlanNum, so this is genuinely a plan-level write. -->
                Benefits are stored per <strong>plan</strong> in Open Dental, so this date applies to
                <strong>every patient on the same plan</strong> — not only the one you verified.
                Like the date above, it is only set when coverage is actually confirmed.
              </div>
            </div>

            <div class="ivs-row ivs-row--bordered">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Communication Log note</div>
                <div class="ivs-row-desc">
                  After a check, add a note to the patient's Communication Log.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.odWriteBackCommlog" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>
            <div v-if="form.odWriteBackCommlog" class="ivs-block ivs-block--indent">
              <div class="ivs-help">
                A note is added for <strong>every</strong> check so the team can see the work —
                including when a plan comes back inactive or can't be verified.
              </div>
            </div>

            <div class="ivs-divider"></div>

            <!-- On-demand -->
            <div class="ivs-row ivs-row--bordered">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Insurance verification on-demand</div>
                <div class="ivs-row-desc">
                  Show a Verify button on the dashboard and patient profile so the team can request
                  another verification anytime.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.onDemandEnabled" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>

            <!-- Flag stale + staleDays -->
            <div class="ivs-row">
              <div class="ivs-row-text">
                <div class="ivs-row-title">Flag stale verifications</div>
                <div class="ivs-row-desc">
                  Alert when insurance was last verified more than {{ form.staleDays }} days ago.
                </div>
              </div>
              <span class="iv-switch">
                <input type="checkbox" v-model="form.flagStale" />
                <span class="iv-switch-slider"></span>
              </span>
            </div>
            <div v-if="form.flagStale" class="ivs-block ivs-block--indent">
              <div class="ivs-inline">
                <span class="ivs-row-title ivs-row-title--sm">Consider stale after</span>
                <input
                  type="number"
                  class="iv-input iv-input--num"
                  min="1"
                  max="365"
                  v-model.number="form.staleDays"
                  @change="clampStaleDays"
                />
                <span class="ivs-unit">days</span>
              </div>
            </div>
          </template>
        </div>

        <!-- Payer-mapping tab -->
        <div v-show="activeTab === 'payers'">
          <PayerMappingList ref="payerList" :site-id="siteId" :active="activeTab === 'payers'" />
        </div>
      </div>

      <!-- Footer -->
      <!--
        The discard confirm REPLACES the normal buttons rather than opening a second dialog:
        a modal-on-a-modal is where the "which one did I just dismiss?" mistakes come from,
        and this keeps the answer in the place the user was already looking.
      -->
      <div v-if="confirmingDiscard" class="ivs-foot ivs-foot--confirm">
        <span class="ivs-foot-warn">
          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
          You have unsaved changes. Close without saving?
        </span>
        <button type="button" class="iv-btn iv-btn--secondary" @click="confirmingDiscard = false">Keep editing</button>
        <button type="button" class="iv-btn iv-btn--danger" @click="discardAndClose">Discard changes</button>
      </div>
      <div v-else class="ivs-foot">
        <button type="button" class="iv-btn iv-btn--secondary" @click="requestClose">Cancel</button>
        <button
          v-if="activeTab === 'settings'"
          type="button"
          class="iv-btn iv-btn--primary"
          :disabled="loading || !!loadError || saving || !!apptFieldError"
          @click="onSave"
        >
          <i v-if="saving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          {{ saving ? 'Saving…' : 'Save settings' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
// Imported explicitly rather than relied on: the other vue-multiselect call sites in this repo
// (SettingsConsent, Campaigns, Workflow2) never import the stylesheet and only look right because
// Routes.js pulls every page into one bundle, so somebody else's import happens to land first.
import 'vue-multiselect/dist/vue-multiselect.min.css';
import PayerMappingList from './PayerMappingList.vue';
import {
  usageMeterView,
  PROCEDURE_CODES_MAX,
  DEFAULT_PROCEDURE_CODES,
  tryAddProcCode,
  buildProcCodeOptions,
  procOptionMapOf,
  procOptionsForSelection,
  seedProcCodesState,
  apptFieldTextError,
  resolveNetworkView,
} from './constants';

// FE twin of insurance-verification-service/lib/settings-defaults.js. Any key
// absent from the stored blob resolves to the default here (keep in lockstep).
// `procedureCodes` DEFAULT is null (NOT the array): null/absent → the Teamio default set
// (DEFAULT_PROCEDURE_CODES below / backend resolveProcedureCodes); a non-empty array is the
// office's explicit CDT list.
const SETTINGS_DEFAULTS = {
  autoPrecheckEnabled: false,
  frequencyMonths: 6,          // legacy shape, still written for an older backend
  reuseWindow: '6m',           // canonical. 'never' | 'Nd' | 'Nm' — see lib/reuse-window.js
  lookaheadDays: 3,
  runTime: '07:00',       // ⛔ NOT 02:00 — payer gateways are least reachable overnight; see the backend twin
  verifyOnBooking: false,
  onDemandEnabled: true,
  flagStale: true,
  staleDays: 30,
  importPdfToOD: false,
  imagingCategoryDefNum: null,
  billingProviderNum: null,
  procedureCodes: null,
  // The settings that let this feature WRITE into the practice's Open Dental. ONE key until
  // 2026-08-18, now three independent ones — `odWriteBack` was NARROWED to the eligibility
  // stamp rather than renamed, so blobs stored before the split keep working.
  //
  // ON by default because a verification the office can't see in OD is half-delivered: staff
  // work in OD and that is where they look.
  //
  // ⛔ MUST match lib/settings-defaults.js INSURANCE_VERIFICATION_DEFAULTS (backend twin) and
  // teamio-admin/seed-insurance-verification.js (seeder twin). A cross-repo test pins all
  // three together; drift here is how a site ends up writing into a customer's OD when
  // nobody meant it to.
  odWriteBack: true,          // Eligibility Last Verified (patient-level), verified results only
  odWriteBackBenefits: false, // Benefits Last Verified (PLAN-level — stamps every patient on the plan), verified only
  odWriteBackCommlog: true,   // Communication Log note, every persisted result
  // "Change value of Appointment Field Defs" (2026-09-23): OFF, nothing chosen. Verified
  // results only, on the checked appointment (Patient Profile: scheduled visits in the next
  // 30 days). Toggle ON requires both strings.
  odWriteBackApptField: false,
  apptFieldName: null,        // OD apptfielddef.FieldName
  apptFieldValue: null,       // the value written into apptfield.FieldValue
  // Which network the verification drawer opens on (2026-09-29): 'both' | 'in' | 'out'.
  // Display only. ⛔ Same lockstep rule as above — the backend 400s an unknown key, so this
  // key must never ship to an environment whose save-settings doesn't know it yet.
  benefitsNetworkView: 'both',
  // Provider-name override on the 270 (2026-10-29). Twin of the backend default only — Super
  // Admin sets it from the SuperAdmin Features card (SuperAdmin.vue), NEVER here: this modal does
  // not render it and buildBlob never posts it (the backend merge keeps whatever is stored).
  // Pinned by cypress/node-tests/insurance-verification-appt-field-writeback.test.js.
  providerNameOverride: null,
};

// benefitsNetworkView choices — values = the backend's BENEFITS_NETWORK_VIEWS.
const NETWORK_VIEW_OPTIONS = [
  { v: 'both', label: 'Both networks' },
  { v: 'in', label: 'In-network only' },
  { v: 'out', label: 'Out-of-network only' },
];

// CDT_CODE_RE, PROCEDURE_CODES_MAX and DEFAULT_PROCEDURE_CODES moved to ./constants.js
// (2026-08-17) along with the option-building and validation helpers, so a node test can
// require them — this file is an SFC and can only ever be checked by regex over its source.

// The backend's FREQUENCY_MONTHS_ALLOWED, with labels.
// The backend REUSE_WINDOW_ALLOWED (lib/reuse-window.js), with labels. One control now spans
// both philosophies: reuse a recent check for a while, or check before every appointment.
// ⛔ Values are STRINGS. Keep them in step with the backend allow-list or /save-settings 400s.
/**
 * Two tiny twins of lib/reuse-window.js, kept here so the modal can round-trip a legacy blob
 * without a server call.
 *
 * ⛔ monthsFromReuseWindow is what stops a NaN reaching the stored blob: buildBlob still
 * writes `frequencyMonths` for an older backend, and Number('7d') is NaN. A window that is
 * not expressible in whole months falls back to the recommended 6 rather than writing junk
 * that a legacy reader would silently treat as 'use the default'.
 */
function monthsToReuseWindow(months) {
  const n = Number(months);
  if (!Number.isFinite(n) || n <= 0) return '6m';
  const w = `${Math.floor(n)}m`;
  return REUSE_OPTIONS.some((o) => o.v === w) ? w : '6m';
}

function monthsFromReuseWindow(win) {
  const m = /^(\d+)m$/.exec(typeof win === 'string' ? win : '');
  return m ? Number(m[1]) : 6;
}

const REUSE_OPTIONS = [
  { v: 'never', label: 'Before every appointment' },
  { v: '7d',    label: 'If older than 7 days' },
  { v: '14d',   label: 'If older than 14 days' },
  { v: '30d',   label: 'If older than 30 days' },
  { v: '1m', label: 'Every month' },
  { v: '2m', label: 'Every 2 months' },
  { v: '3m', label: 'Every 3 months' },
  { v: '4m', label: 'Every 4 months' },
  { v: '5m', label: 'Every 5 months' },
  { v: '6m', label: 'Every 6 months' },
  { v: '9m', label: 'Every 9 months' },
  { v: '12m', label: 'Every 12 months' },
  { v: '18m', label: 'Every 18 months' },
  { v: '24m', label: 'Every 24 months' },
];

function clampInt(v, lo, hi, fallback) {
  let n = parseInt(v, 10);
  if (!Number.isFinite(n)) n = fallback;
  if (n < lo) n = lo;
  if (n > hi) n = hi;
  return n;
}

export default {
  name: 'PrecheckSettingsModal',
  components: { PayerMappingList, Multiselect },
  props: {
    open: { type: Boolean, default: false },
    siteId: { type: String, default: '' },
  },

  data() {
    return {
      activeTab: 'settings',
      form: Object.assign({}, SETTINGS_DEFAULTS),
      loading: false,
      loadError: null,
      saving: false,
      imagingCategories: [],
      imagingLoading: false,
      // The office's OD Appointment Field Defs ([{ apptFieldDefNum, fieldName }]).
      apptFieldDefs: [],
      apptFieldLoading: false,
      reuseOptions: REUSE_OPTIONS,
      networkViewOptions: NETWORK_VIEW_OPTIONS,
      // Procedure-code chip editor. `procCustomized` = the office has an explicit list
      // (saved as an array); false = on the Teamio default set (saved as null). `procCodes`
      // holds the chips currently shown (the default set until the office edits).
      procCodes: DEFAULT_PROCEDURE_CODES.slice(),
      procCustomized: false,
      procError: null,
      // A REAL flag, deliberately not `!procCodeOptions.length` the way SettingsConsent does
      // it — that spins forever when the fetch fails or the office genuinely has no codes.
      procLoading: false,
      procLoadFailed: false,
      // Serialized buildBlob() as it stood when the settings finished loading. Comparing the
      // SAVE PAYLOAD rather than tracking a dirty flag per control means a new setting is
      // covered the day it is added, and it can't drift out of sync with what Save sends.
      // Null until a load succeeds — a failed load must not make the modal un-closeable.
      baselineBlob: null,
      confirmingDiscard: false,
      // Billed-check meter for the current site-local month. Null until it loads, and
      // it STAYS null on failure — the meter is informational and must never block the
      // settings it sits above.
      usage: null,
    };
  },

  computed: {
    ...mapState({
      providers: (state) => (state.customer && state.customer.providers) || [],
      user: (state) => state.customer && state.customer.user,
      odProcCodes: (state) => (state.customer && state.customer.procCodes) || [],
    }),

    // The dropdown's options: the office's own Open Dental codes, plus Teamio's default set,
    // plus anything already selected. See buildProcCodeOptions for why `value` is the
    // ProcCode and never the OD CodeNum.
    procCodeOptions() {
      return buildProcCodeOptions(this.odProcCodes, this.procCodes);
    },
    procOptionMap() {
      return procOptionMapOf(this.procCodeOptions);
    },
    // Chips. `procCodes` stays the source of truth — this only decorates it with labels, and
    // falls back for a code the office no longer carries so it can never vanish silently.
    selectedProcOptions() {
      return procOptionsForSelection(this.procCodes, this.procOptionMap);
    },

    // Has the Automation tab been edited since it loaded? Compares the serialized save
    // payload, so it is exactly "would Save write something different".
    settingsDirty() {
      if (this.baselineBlob === null) return false;
      return JSON.stringify(this.buildBlob()) !== this.baselineBlob;
    },

    // Belt-and-suspenders admin gate (mirrors InsuranceVerification.vue's
    // isAdminOrAbove — Admin/Super-Admin convention). The button that opens
    // this modal is already admin-only; this stops the modal from rendering or
    // fetching if `open` were forced true for a non-admin.
    isAdminOrAbove() {
      return !!(this.user && (this.user.Role === 'Admin' || this.user.Role === 'Super Admin'));
    },

    /*
     * ⚠ `canEditOdWriteBack` was REMOVED on 2026-08-18 with the three-way split, deliberately.
     *
     * It gated the old single write-back toggle to Super Admin. Two reasons it went:
     *   1. It was UI-only and never a security boundary — `saveSettings` takes
     *      `{ siteID, settings }` and carries no user or role context at all, so anyone who
     *      could reach the endpoint could already set the key. It bought no safety.
     *   2. The whole point of splitting these toggles was to let an office decide what lands
     *      in its own chart. Three locked toggles would have been three support calls instead
     *      of one.
     *
     * If a lock is ever wanted again, it belongs on the ENDPOINT, not on the control — see the
     * endpoint-authorization gap tracked for this service.
     */

    // Shared usage-meter view. The band / plural / split rules live in one pure formatter
    // (constants.js `usageMeterView`) so this modal and the Credits-tab Usage card render the
    // exact same money-copy. Null until the meter loads — the whole block is v-if="usage".
    usageView() {
      return this.usage ? usageMeterView(this.usage.counts) : null;
    },

    providerOptions() {
      const list = Array.isArray(this.providers) ? this.providers.slice() : [];
      list.sort((a, b) => {
        const an = `${a.LName || ''} ${a.FName || ''}`.trim().toLowerCase();
        const bn = `${b.LName || ''} ${b.FName || ''}`.trim().toLowerCase();
        return an.localeCompare(bn);
      });
      return list.map((p) => ({ provNum: p.ProvNum, label: this.providerLabel(p) }));
    },

    // ── Cost warnings ──────────────────────────────────────────────────────
    // Both of these are informational, NOT gates. The backend already bounds the
    // ranges (frequencyMonths to a whitelist, lookaheadDays 0-14); tier-based limits
    // are deliberately not implemented here because no pricing tier exists in the
    // product yet, and hard-coding one would bake in a billing model that hasn't been
    // decided.

    /**
     * Anything shorter than the recommended 6 months multiplies re-verification volume.
     *
     * ⛔ Reads the WINDOW, not a number. `Number('7d')` is NaN and `Number('never')` is NaN,
     * so the old numeric comparison silently reported every new short window as CHEAP —
     * suppressing the cost warning on exactly the settings that need it most.
     */
    frequencyIsExpensive() {
      const w = this.form.reuseWindow;
      if (w === 'never') return true;
      const m = /^(\d+)([dm])$/.exec(typeof w === 'string' ? w : '');
      if (!m) return false;
      return m[2] === 'd' ? true : Number(m[1]) < 6;
    },

    /**
     * The reuse window as a DURATION PHRASE, for sentences that used to hardcode "months".
     *
     * ⛔ This replaced `{{ form.frequencyMonths || 6 }} months`, which renders "7 months" for a
     * 7-day window and "never months" for the appointment mode — a sentence about how long a
     * wrong result would be trusted, stating the wrong length.
     */
    reuseWindowLabel() {
      const w = this.form.reuseWindow;
      if (w === 'never') return 'this appointment';
      const m = /^(\d+)([dm])$/.exec(typeof w === 'string' ? w : '');
      if (!m) return '6 months';
      const n = Number(m[1]);
      const unit = m[2] === 'd' ? 'day' : 'month';
      return `${n} ${unit}${n === 1 ? '' : 's'}`;
    },

    // 7+ days is where a FAILING patient starts getting retried on several days.
    lookaheadIsLong() {
      const n = Number(this.form.lookaheadDays);
      return Number.isFinite(n) && n > 7;
    },

    // Selects bind through a proxy so a "" from an empty native option coerces
    // cleanly to null (the DB "unset" value), never the string "".
    billingProviderModel: {
      get() { return this.form.billingProviderNum; },
      set(v) { this.form.billingProviderNum = (v === '' || v === undefined) ? null : v; },
    },
    imagingCategoryModel: {
      get() { return this.form.imagingCategoryDefNum; },
      set(v) { this.form.imagingCategoryDefNum = (v === '' || v === undefined) ? null : v; },
    },
    apptFieldNameModel: {
      get() { return this.form.apptFieldName; },
      set(v) { this.form.apptFieldName = (v === '' || v === undefined) ? null : v; },
    },

    // A stored FieldName the office has since renamed/deleted in OD. Kept selectable (so the
    // select doesn't silently blank it) but warned — the write would just match nothing.
    apptFieldNameMissing() {
      const name = this.form.apptFieldName;
      if (!name || this.apptFieldLoading || !this.apptFieldDefs.length) return false;
      return !this.apptFieldDefs.some((f) => f.fieldName === name);
    },

    /**
     * Blocks Save (inline, next to the control) when the toggle is ON without a usable pair.
     * Mirrors the backend's save-settings rule so the optimistic save can't fail after the
     * modal has already closed. Toggle OFF → never blocks (buildBlob drops unusable strings).
     */
    apptFieldError() {
      const f = this.form;
      if (!f.odWriteBackApptField) return null;
      if (!f.apptFieldName) return 'Pick the appointment field to set.';
      const nameErr = apptFieldTextError(f.apptFieldName);
      if (nameErr) return `The appointment field name ${nameErr}.`;
      if (f.apptFieldValue == null || !String(f.apptFieldValue).trim()) return 'Enter the value to set.';
      const valueErr = apptFieldTextError(f.apptFieldValue);
      if (valueErr) return `The value ${valueErr}.`;
      return null;
    },
  },

  watch: {
    open: {
      immediate: true,
      handler(now) {
        if (now) this.onOpen();
      },
    },
  },

  methods: {
    ...mapActions('insuranceVerification', ['fetchSettings', 'saveSettings', 'fetchImagingCategories', 'fetchApptFieldDefs', 'fetchUsage']),

    onOpen() {
      // Defense in depth: a non-admin should never reach here (the opener
      // button and openSettings() are both admin-gated, and the root v-if
      // won't render). If `open` were forced true anyway, bail without
      // fetching and ask the parent to close.
      if (!this.isAdminOrAbove) {
        this.$emit('close');
        return;
      }
      this.activeTab = 'settings';
      this.saving = false;
      this.confirmingDiscard = false;
      // Cleared BEFORE the reload so a reopen can't compare against the previous session's
      // snapshot and report a phantom edit.
      this.baselineBlob = null;
      this.loadSettings(true);
      this.loadImagingCategories();
      this.loadApptFieldDefs();
      this.loadUsage();
      this.ensureProcCodes();
    },

    /**
     * Load the office's Open Dental procedure codes for the picker.
     *
     * `customer.procCodes` is NOT loaded at app boot — only Campaigns / Huddle / PracticePulse /
     * Settings dispatch it — so an admin who came straight here on a fresh browser profile would
     * otherwise see an empty list. Cached in Vuex + localStorage, so this is usually a no-op.
     *
     * Deliberately fire-and-forget: the codes decorate the picker, and the picker still accepts
     * typed codes without them. This must never gate loadSettings.
     */
    async ensureProcCodes() {
      if (!this.siteId) return;
      const cached = this.odProcCodes;
      if (Array.isArray(cached) && cached.length > 0) return;
      this.procLoading = true;
      this.procLoadFailed = false;
      try {
        const rows = await this.$store.dispatch('customer/getProcCodes', { siteID: this.siteId });
        // getProcCodes swallows its own errors and resolves [] (it dispatches the global alert
        // instead of rethrowing), so an empty result is the only failure signal available here.
        this.procLoadFailed = !Array.isArray(rows) || rows.length === 0;
      } catch (e) {
        this.procLoadFailed = true;
      } finally {
        this.procLoading = false;
      }
    },

    // The meter is decoration on top of the settings, never a precondition for them:
    // fetchUsage resolves null instead of throwing, and a null simply hides the panel.
    async loadUsage() {
      if (!this.siteId) return;
      this.usage = null;
      const data = await this.fetchUsage({ siteID: this.siteId });
      // Guard the shape — an older backend without /usage answers a 4xx, which
      // fetchUsage turns into null, but a partial payload must not blow up the render.
      this.usage = (data && data.counts && data.counts.reconciliation && data.counts.byTrigger)
        ? data
        : null;
    },

    // Billable count for one trigger key off the shared view (0 when the segment is absent).
    // The modal shows BILLABLE-only numbers by design — the per-trigger TOTALS are for the
    // Credits-tab card, not here — so this reads split[].billable and ignores split[].total.
    splitBillable(key) {
      const v = this.usageView;
      if (!v) return 0;
      const entry = v.split.find((s) => s.key === key);
      return entry ? entry.billable : 0;
    },

    providerLabel(p) {
      const name = [p.LName, p.FName].filter(Boolean).join(', ');
      const abbr = p.Abbr ? ` (${p.Abbr})` : '';
      if (name) return name + abbr;
      return p.Abbr || `Provider ${p.ProvNum}`;
    },

    async loadSettings(force = false) {
      if (!this.siteId) return;
      this.loading = true;
      this.loadError = null;
      try {
        const data = await this.fetchSettings({ siteID: this.siteId, force });
        const stored = (data && data.settings) || {};
        // defaults ⊕ stored, restricted to known keys.
        const next = {};
        Object.keys(SETTINGS_DEFAULTS).forEach((k) => {
          next[k] = stored[k] !== undefined ? stored[k] : SETTINGS_DEFAULTS[k];
        });
        // ⛔ COMPATIBLE READ, mirroring lib/reuse-window.js resolveReuseWindow. Every site on
        // prod stores `frequencyMonths` and no `reuseWindow`. Without this the loop above
        // would seed the DEFAULT '6m' for a site actually running 12 months, and the modal
        // would quietly display — and on save, write — a window the office never chose.
        if (stored.reuseWindow === undefined) {
          next.reuseWindow = monthsToReuseWindow(stored.frequencyMonths);
        }
        this.form = next;
        this.seedProcedureCodes(stored.procedureCodes);
        // Snapshot AFTER seeding, so the loaded state is by definition not dirty.
        this.baselineBlob = JSON.stringify(this.buildBlob());
      } catch (err) {
        this.loadError = (err && err.message) || 'Failed to load settings';
        // Stays null on failure: there is nothing to lose, so the close guard must not
        // trap the user inside a modal that couldn't even load.
        this.baselineBlob = null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * The single close path for the scrim, the X and Cancel.
     *
     * Anything unsaved arms the footer confirm instead of closing. The alternative — closing
     * and toasting "changes discarded" — is worse here: the settings blob is not recoverable
     * from anywhere in the UI, so the only honest moment to ask is before it is gone.
     */
    requestClose() {
      if (this.hasUnsavedWork()) {
        this.confirmingDiscard = true;
        return;
      }
      this.$emit('close');
    },

    // Both tabs. The payer tab saves per row, so it answers for its own drafts.
    hasUnsavedWork() {
      if (this.settingsDirty) return true;
      const list = this.$refs.payerList;
      return !!(list && typeof list.hasUnsavedEdits === 'function' && list.hasUnsavedEdits());
    },

    discardAndClose() {
      this.confirmingDiscard = false;
      this.$emit('close');
    },

    async loadImagingCategories() {
      if (!this.siteId) return;
      this.imagingLoading = true;
      try {
        this.imagingCategories = await this.fetchImagingCategories({ siteID: this.siteId }) || [];
      } catch (err) {
        // Non-fatal — the folder select just shows the empty-state hint.
        console.error('[PrecheckSettingsModal] imaging categories:', err);
        this.imagingCategories = [];
      } finally {
        this.imagingLoading = false;
      }
    },

    async loadApptFieldDefs() {
      if (!this.siteId) return;
      this.apptFieldLoading = true;
      try {
        this.apptFieldDefs = await this.fetchApptFieldDefs({ siteID: this.siteId }) || [];
      } catch (err) {
        // Non-fatal — the field select just shows the empty-state hint.
        console.error('[PrecheckSettingsModal] appointment field defs:', err);
        this.apptFieldDefs = [];
      } finally {
        this.apptFieldLoading = false;
      }
    },

    // Seed the chip editor from the loaded blob: a non-empty array of valid CDT codes means
    // the office has customized (chips = that list); null/absent/empty means it is on the
    // Teamio default set (chips = the default set, shown as the editable starting point).
    seedProcedureCodes(stored) {
      const seeded = seedProcCodesState(stored);
      this.procCodes = seeded.codes;
      this.procCustomized = seeded.customized;
      this.procError = null;
    },

    // Free-text path — the office typed a code that isn't in their Open Dental. Kept because
    // these codes go to the PAYER, not to OD: a practice can legitimately ask about a code they
    // have never charted. Validation is identical to the dropdown path.
    onProcTag(raw) {
      const res = tryAddProcCode(this.procCodes, raw);
      if (!res.ok) { this.procError = res.error; return; }
      this.procCodes = this.procCodes.concat(res.code);
      this.procCustomized = true;
      this.procError = null;
    },

    // Dropdown path — vue-multiselect hands back the full selection, including removals made
    // by clicking a checked row. The cap is enforced here rather than via the `:max` prop so
    // both paths surface the same message, and because `:max` does not gate tagging.
    onProcSelectionChange(opts) {
      const next = [];
      (opts || []).forEach((o) => {
        const code = o && o.value;
        if (code && next.indexOf(code) === -1) next.push(code);
      });
      if (next.length > PROCEDURE_CODES_MAX) {
        this.procError = `You can add at most ${PROCEDURE_CODES_MAX} codes.`;
        return;
      }
      this.procCodes = next;
      this.procCustomized = true;
      this.procError = null;
    },

    removeProcedureCode(code) {
      this.procCodes = this.procCodes.filter((c) => c !== code);
      this.procCustomized = true;
      this.procError = null;
    },

    resetProcedureCodes() {
      this.procCodes = DEFAULT_PROCEDURE_CODES.slice();
      this.procCustomized = false;
      this.procError = null;
    },

    bumpLookahead(delta) {
      this.form.lookaheadDays = clampInt(this.form.lookaheadDays + delta, 0, 14, SETTINGS_DEFAULTS.lookaheadDays);
    },

    clampStaleDays() {
      this.form.staleDays = clampInt(this.form.staleDays, 1, 365, SETTINGS_DEFAULTS.staleDays);
    },

    buildBlob() {
      const f = this.form;
      return {
        autoPrecheckEnabled: !!f.autoPrecheckEnabled,
        // ⛔ BOTH keys are written. `reuseWindow` is canonical; `frequencyMonths` stays so an
        // older backend (mid-deploy, or a rollback) keeps reading a sane window instead of
        // falling back to its default. Derived from the window when it is expressible in
        // months, else left at the recommended 6 — never NaN.
        frequencyMonths: monthsFromReuseWindow(f.reuseWindow),
        reuseWindow: REUSE_OPTIONS.some((o) => o.v === f.reuseWindow)
          ? f.reuseWindow
          : SETTINGS_DEFAULTS.reuseWindow,
        lookaheadDays: clampInt(f.lookaheadDays, 0, 14, SETTINGS_DEFAULTS.lookaheadDays),
        runTime: f.runTime || SETTINGS_DEFAULTS.runTime,
        verifyOnBooking: !!f.verifyOnBooking,
        onDemandEnabled: !!f.onDemandEnabled,
        flagStale: !!f.flagStale,
        staleDays: clampInt(f.staleDays, 1, 365, SETTINGS_DEFAULTS.staleDays),
        importPdfToOD: !!f.importPdfToOD,
        odWriteBack: !!f.odWriteBack,
        odWriteBackBenefits: !!f.odWriteBackBenefits,
        odWriteBackCommlog: !!f.odWriteBackCommlog,
        odWriteBackApptField: !!f.odWriteBackApptField,
        // Trimmed; anything unusable goes as null (the backend 400s a non-null bad string even
        // with the toggle OFF). Toggle ON with a bad pair never gets here — apptFieldError
        // blocks Save first.
        apptFieldName: apptFieldTextError(f.apptFieldName) ? null : f.apptFieldName.trim(),
        apptFieldValue: apptFieldTextError(f.apptFieldValue) ? null : f.apptFieldValue.trim(),
        imagingCategoryDefNum: f.imagingCategoryDefNum != null ? Number(f.imagingCategoryDefNum) : null,
        billingProviderNum: f.billingProviderNum != null ? Number(f.billingProviderNum) : null,
        // null = on the Teamio default set; a non-empty custom list = the office's explicit
        // codes. An emptied custom list also saves as null (backend resolves it to the default).
        procedureCodes: (this.procCustomized && this.procCodes.length) ? this.procCodes.slice() : null,
        benefitsNetworkView: resolveNetworkView(f.benefitsNetworkView),
      };
    },

    // Optimistic close: build the blob, close immediately, persist in the
    // background. Success updates the store cache (dashboard staleDays/flagStale
    // react); a failure surfaces as a toast so the user knows to retry.
    onSave() {
      // The button is disabled too; this is the belt to that brace (an Enter keypress etc.).
      if (this.apptFieldError) return;
      const blob = this.buildBlob();
      const siteID = this.siteId;
      this.saving = true;
      this.$emit('saved', blob);
      this.$emit('close');
      this.saveSettings({ siteID, settings: blob })
        .then(() => {
          if (this.$toast) this.$toast.success('Precheck settings saved');
        })
        .catch((err) => {
          if (this.$toast) this.$toast.error((err && err.message) || 'Couldn’t save settings — please try again.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes ivsScrim { from { opacity: 0; } to { opacity: 1; } }
@keyframes ivsPop { from { transform: scale(0.97); opacity: 0.5; } to { transform: scale(1); opacity: 1; } }

.ivs-scrim {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(12, 15, 14, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ivsScrim 0.2s ease;
}

.ivs-modal {
  width: min(560px, 100%);
  max-height: calc(100vh - 48px);
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(12, 15, 14, 0.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: #0c0f0e;
  animation: ivsPop 0.24s cubic-bezier(0.16, 1, 0.3, 1);
}

// ── Header ──────────────────────────────────────────────
.ivs-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #dfe4e3;
}

.ivs-head-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #e9f6f1;
  color: #0f7259;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex: none;
}

.ivs-head-meta { flex: 1; min-width: 0; }

.ivs-title {
  margin: 0;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: -0.02em;
}

.ivs-sub {
  margin: 2px 0 0;
  font-size: 13px;
  color: #717977;
}

.ivs-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #717977;
  border-radius: 8px;
  cursor: pointer;
  flex: none;

  &:hover { background: #eceff0; color: #0c0f0e; }
}

// ── Tabs ────────────────────────────────────────────────
.ivs-tabs {
  flex: none;
  display: flex;
  gap: 4px;
  padding: 10px 22px 0;
  border-bottom: 1px solid #dfe4e3;
}

.ivs-tab {
  border: none;
  background: transparent;
  padding: 9px 14px;
  font-size: 13.5px;
  font-weight: 700;
  color: #717977;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;

  &:hover { color: #0c0f0e; }
  &.is-active { color: #0f7259; border-bottom-color: #0f7259; }
}

// ── Body ────────────────────────────────────────────────
.ivs-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px 22px 12px;
  min-height: 0;
}

.ivs-state {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  padding: 40px 8px;
  color: #717977;
  font-size: 13.5px;

  &--error { color: #a5292e; }
}

.ivs-retry {
  margin-left: 8px;
  border: 1px solid #e5484d;
  background: #fff;
  color: #a5292e;
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.ivs-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 0;

  &--bordered { border-bottom: 1px solid #dfe4e3; }
}

.ivs-row-text { flex: 1; min-width: 0; }

.ivs-row-title {
  font-weight: 700;
  font-size: 14.5px;
  color: #0c0f0e;

  &--lg { font-weight: 800; font-size: 15.5px; }
  &--sm { font-size: 13.5px; }
}

.ivs-row-desc {
  font-size: 12.5px;
  color: #717977;
  margin-top: 2px;
  line-height: 1.5;
}

.ivs-block {
  padding: 14px 0 16px;

  &--bordered { border-bottom: 1px solid #dfe4e3; }
  &--indent { padding: 4px 0 14px 2px; }
}

.ivs-control {
  margin-top: 8px;

  &--sm { max-width: 160px; }
  &--md { max-width: 260px; }
}

// Second label inside the Appointment Field Defs indent block (field → value).
.ivs-appt-value-label { margin-top: 14px; }

.ivs-help {
  font-size: 12px;
  line-height: 1.6;
  color: #717977;
  margin-top: 10px;

  &--warn { color: #b45309; }
}

.ivs-subsection {
  transition: opacity 0.16s ease;

  &.is-off { opacity: 0.55; }
}

// ── Usage meter ─────────────────────────────────────────
.ivs-usage {
  border: 1px solid #dfe4e3;
  border-radius: 8px;
  background: #f7f9f9;
  padding: 12px 14px;
  margin-bottom: 16px;
}

.ivs-usage-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}

.ivs-usage-title {
  font-size: 12.5px;
  font-weight: 600;
  color: #4a524f;
}

.ivs-usage-month {
  font-size: 11.5px;
  color: #8a918f;
}

.ivs-usage-figure {
  margin-top: 4px;
  font-size: 22px;
  line-height: 1.2;
  color: #262b2a;
}

.ivs-usage-unit {
  margin-left: 6px;
  font-size: 12.5px;
  color: #717977;
}

.ivs-usage-split {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 12px;
  color: #717977;
}

.ivs-usage-note {
  margin-top: 7px;
  font-size: 11.5px;
  line-height: 1.5;
  color: #8a918f;
}

// ── Cost warning (a setting that costs money) ───────────
// Amber, not red: nothing is wrong, but the office is about to spend more.
.ivs-cost-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid #f2e0b0;
  border-radius: 6px;
  background: #fff8e6;
  color: #8a6100;
  font-size: 12px;
  line-height: 1.55;

  i { margin-top: 2px; }
}

.ivs-divider {
  height: 1px;
  background: #dfe4e3;
  margin: 6px 0;
}

// Groups the three Open Dental write-back toggles under one heading, so they read as one
// decision with three parts rather than three unrelated switches that happen to sit together.
.ivs-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0f7259;
  margin: 4px 0 2px;
}

.ivs-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ivs-unit {
  font-size: 13px;
  color: #717977;
}

// ── Stepper ─────────────────────────────────────────────
.ivs-stepper {
  display: flex;
  align-items: center;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
  overflow: hidden;
  flex: none;
}

.ivs-step {
  width: 36px;
  height: 36px;
  border: none;
  background: #ffffff;
  font-size: 18px;
  font-weight: 700;
  color: #717977;
  cursor: pointer;

  &:not(:disabled):hover { background: #eceff0; color: #0c0f0e; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.ivs-step-val {
  width: 56px;
  text-align: center;
  font-weight: 800;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  border-left: 1px solid #dfe4e3;
  border-right: 1px solid #dfe4e3;
}

// ── Inputs ──────────────────────────────────────────────
.iv-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #c8cfcd;
  border-radius: 9px;
  font-size: 13.5px;
  color: #0c0f0e;
  background: #ffffff;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0f7259;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }
}

.iv-input--num { width: 88px; }

// ── Procedure-code picker ───────────────────────────────
//
// ⛔ CONTAINMENT: every ::v-deep below is nested INSIDE .ivs-proc-select on purpose. A
// top-level ::v-deep on a third-party component's classes (.multiselect__*) compiles to a
// selector with no scope attribute and would restyle every other vue-multiselect in the app
// — Campaigns, Workflow2, Settings and the Tx Presenter consent picker all use it. Same rule
// ConsentMappingModal.vue documents for its own .cm-editor block. Keep new rules nested.
//
// ::v-deep, not /deep/ (deprecated) and not :deep() (misbehaves with vue-multiselect here).
.ivs-proc-select {
  margin-top: 12px;

  ::v-deep .multiselect__tags {
    min-height: 38px;
    padding: 6px 40px 2px 8px;
    border: 1px solid #d7dedb;
    border-radius: 9px;
    font-size: 13px;
  }

  ::v-deep .multiselect--active .multiselect__tags {
    border-color: #0f7259;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
  }

  ::v-deep .multiselect__input,
  ::v-deep .multiselect__placeholder,
  ::v-deep .multiselect__single {
    font-size: 13px;
    padding: 0;
    margin-bottom: 4px;
    background: transparent;
  }

  // Cosmetic only — the real normalization is in normalizeCdtInput.
  ::v-deep .multiselect__input { text-transform: uppercase; }

  // The chips are our own .ivs-chip markup via the tag slot, so strip the library's tag
  // styling and just handle spacing.
  ::v-deep .multiselect__tag { background: none; padding: 0; margin: 0 6px 4px 0; }

  ::v-deep .multiselect__option--highlight {
    background: #0f7259;
    color: #fff;
  }
  ::v-deep .multiselect__option--selected {
    background: #e9f6f1;
    color: #0b5c49;
    font-weight: 700;
  }
  ::v-deep .multiselect__option--selected.multiselect__option--highlight {
    background: #0b5c49;
    color: #fff;
  }

  // Long "D3310 - endodontic therapy, anterior tooth …" labels must not widen the control.
  ::v-deep .multiselect__option {
    font-size: 13px;
    min-height: 34px;
    padding: 9px 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep .multiselect__spinner:after,
  ::v-deep .multiselect__spinner:before { border-top-color: #0f7259; }
}

.ivs-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 6px 0 11px;
  background: #e9f6f1;
  color: #0b5c49;
  border: 1px solid #bfe4d8;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.ivs-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  color: #0b5c49;
  border-radius: 50%;
  font-size: 10px;
  cursor: pointer;
  opacity: 0.7;

  &:hover { background: #cdeadf; opacity: 1; }
}

.ivs-proc-add {
  margin-top: 10px;
  flex-wrap: wrap;
}

.iv-btn--sm {
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
}

.ivs-linkbtn {
  border: none;
  background: transparent;
  color: #0f7259;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 4px;

  &:hover { text-decoration: underline; }
}

/* Native <select> renders the selected value white-on-white inside a custom-themed
   input background — force our own chevron + appearance:none so the text shows.
   (Canonical select-clip fix; targets <select class="iv-input">.) */
select.iv-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 0 32px 0 12px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5l3 3 3-3'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 12px;
  cursor: pointer;
}
select.iv-input:disabled {
  background-color: #f1f3f6;
  color: #94a3b8;
  cursor: not-allowed;
}

// ── Toggle switch (input-on-top so IT is the click target — no reliance on
//    <label> click-forwarding; the reliable .txb-switch pattern). ────────────
.iv-switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex: none;
}
.iv-switch input[type="checkbox"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}
.iv-switch-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.16s ease;
  cursor: pointer;
}
.iv-switch-slider::before {
  content: '';
  position: absolute;
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background: #ffffff;
  border-radius: 50%;
  transition: transform 0.16s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.iv-switch input:checked + .iv-switch-slider { background: #0f7259; }
.iv-switch input:checked + .iv-switch-slider::before { transform: translateX(20px); }

// ── Footer ──────────────────────────────────────────────
.ivs-foot {
  flex: none;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  background: #fafbfb;
  border-top: 1px solid #dfe4e3;
}

.iv-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 16px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.iv-btn--secondary {
  border: 1px solid #c8cfcd;
  background: #ffffff;
  color: #0b5c49;

  &:not(:disabled):hover { border-color: #0b5c49; background: #e9f6f1; }
}

.iv-btn--primary {
  border: 1px solid #0f7259;
  background: #0f7259;
  color: #ffffff;

  &:not(:disabled):hover { background: #0b5c49; border-color: #0b5c49; }
}

// Discarding is the destructive choice here, so it is the one that reads as destructive —
// and it is deliberately NOT the primary-styled button, so a reflexive "click the green one"
// keeps your work rather than throwing it away.
.iv-btn--danger {
  border: 1px solid #e5484d;
  background: #ffffff;
  color: #c62a2f;

  &:not(:disabled):hover { background: #fdeeee; border-color: #c62a2f; }
}

.ivs-foot--confirm {
  justify-content: flex-end;
  flex-wrap: wrap;
  background: #fef6e7;
  border-top: 1px solid #f5d99b;
}

.ivs-foot-warn {
  flex: 1 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #b45309;
}
</style>
